import React, { useState, useEffect, useRef } from 'react';

const NumberField = ({
  id,
  label,
  name,
  value = '',
  onChange,
  labelClass,
  disabled = false,
  isDynamic = false,
  placeholder = '',
  required = false,
  showCurrency = false,
  step = 1,
  hint, // ✅ Hint popup support
}) => {
  // console.log("id", id, hint)
  const [numberValues, setNumberValues] = useState(
    isDynamic ? (Array.isArray(value) ? value : []) : value
  );
  const [showHint, setShowHint] = useState(false);
  const hintRef = useRef(null);
  const labelClasses = `${labelClass || ''} mb-0`;

  /* ---------------- SYNC VALUE ---------------- */
  useEffect(() => {
    if (isDynamic) {
      setNumberValues(Array.isArray(value) ? value : []);
    } else {
      setNumberValues(value);
    }
  }, [value, isDynamic]);

  /* ---------------- CLOSE HINT ON OUTSIDE CLICK ---------------- */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (hintRef.current && !hintRef.current.contains(e.target)) {
        setShowHint(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* ---------------- HANDLERS ---------------- */
  const handleDynamicChange = (index, newValue) => {
    const updatedValues = [...numberValues];
    updatedValues[index] = newValue;
    setNumberValues(updatedValues);
    onChange(id, updatedValues);
  };

  const handleAddField = () => {
    const updatedValues = [...numberValues, ''];
    setNumberValues(updatedValues);
    onChange(id, updatedValues);
  };

  const handleRemoveField = (index) => {
    const updatedValues = numberValues.filter((_, idx) => idx !== index);
    setNumberValues(updatedValues);
    onChange(id, updatedValues);
  };

  const handleStaticChange = (e) => {
    onChange(id, e.target.value);
  };

  const getDisplayedNumbers = () => {
    if (!showCurrency)
      return [];
    if (isDynamic) {
      return (Array.isArray(numberValues) ? numberValues : [])
        .map((val) => `${val}`.trim())
        .filter((val) => val.length > 0);
    }

    const asText = `${value}`.trim();
    return asText.length > 0 ? [asText] : [];
  };

  const numberToIndianWords = (inputNumber) => {
    const number = Math.floor(Math.abs(inputNumber));
    if (number === 0) return 'zero';

    const ones = [
      '',
      'one',
      'two',
      'three',
      'four',
      'five',
      'six',
      'seven',
      'eight',
      'nine',
      'ten',
      'eleven',
      'twelve',
      'thirteen',
      'fourteen',
      'fifteen',
      'sixteen',
      'seventeen',
      'eighteen',
      'nineteen',
    ];
    const tens = [
      '',
      '',
      'twenty',
      'thirty',
      'forty',
      'fifty',
      'sixty',
      'seventy',
      'eighty',
      'ninety',
    ];

    const twoDigits = (n) => {
      if (n === 0) return '';
      if (n < 20) return ones[n];
      const ten = Math.floor(n / 10);
      const unit = n % 10;
      return unit ? `${tens[ten]} ${ones[unit]}` : tens[ten];
    };

    const threeDigits = (n) => {
      const hundred = Math.floor(n / 100);
      const rest = n % 100;
      const hundredText = hundred ? `${ones[hundred]} hundred` : '';
      const restText = twoDigits(rest);
      if (hundredText && restText) return `${hundredText} ${restText}`;
      return hundredText || restText;
    };

    const parts = [];
    const crore = Math.floor(number / 10000000);
    const lakh = Math.floor((number % 10000000) / 100000);
    const thousand = Math.floor((number % 100000) / 1000);
    const remainder = number % 1000;

    if (crore) parts.push(`${threeDigits(crore)} crore`);
    if (lakh) parts.push(`${threeDigits(lakh)} lakh`);
    if (thousand) parts.push(`${threeDigits(thousand)} thousand`);
    if (remainder) parts.push(threeDigits(remainder));

    return parts.join(' ').trim();
  };

  const numberToIndianCurrencyWords = (inputNumber) => {
    const isNegative = inputNumber < 0;
    const absoluteValue = Math.abs(inputNumber);
    const rupees = Math.floor(absoluteValue);
    const paise = Math.round((absoluteValue - rupees) * 100);

    const rupeeWords = numberToIndianWords(rupees);
    const paiseWords = paise ? numberToIndianWords(paise) : '';

    const rupeeLabel = rupees === 1 ? 'rupee' : 'rupees';
    const paiseLabel = paise === 1 ? 'paise' : 'paise';

    const rupeePart = `${rupeeWords} ${rupeeLabel}`;
    const paisePart = paise ? ` and ${paiseWords} ${paiseLabel}` : '';
    const sign = isNegative ? 'minus ' : '';

    return `${sign}${rupeePart}${paisePart}`;
  };

  const displayedNumbers = getDisplayedNumbers();
  const displayedRupees = displayedNumbers
    .map((val) => {
      const trimmed = `${val}`.trim();
      if (!trimmed) return '';
      const numericValue = Number(trimmed);
      if (!Number.isFinite(numericValue)) return `${trimmed}`;
      return numberToIndianCurrencyWords(numericValue);
    })
    .filter(Boolean);

  /* ---------------- RENDER ---------------- */
  return (
    <div className="form-group">
      {label && (
        <div className="d-inline-flex align-items-center gap-2 mb-1 position-relative flex-nowrap">
          <label htmlFor={name} className={labelClasses}>
            {label}
            {required && <span className="text-danger ms-1">*</span>}
          </label>

          {hint && (
            <div className="position-relative" ref={hintRef}>
              <span
                role="button"
                tabIndex={0}
                className="text-muted"
                style={{ cursor: 'pointer' }}
                aria-label="Show hint"
                onClick={() => setShowHint((prev) => !prev)}
                onKeyDown={(e) =>
                  e.key === 'Enter' && setShowHint((prev) => !prev)
                }
              >
                ℹ️
              </span>

              {showHint && (
                <div
                  className="position-absolute bg-white border shadow rounded p-2"
                  style={{
                    top: '120%',
                    left: 0,
                    width: '260px',
                    zIndex: 3000,
                  }}
                >
                  <div className="small text-muted">{hint}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {isDynamic ? (
        <div>
          {numberValues.length === 0 && (
            <div className="d-flex align-items-center mb-2">
              <input
                type="number"
                className="form-control me-2"
                placeholder={placeholder}
                disabled={disabled}
                step={step}
                required={required}
                onWheel={(e) => e.currentTarget.blur()}
              />
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={handleAddField}
                disabled={disabled}
              >
                Add
              </button>
            </div>
          )}

          {numberValues.map((num, index) => (
            <div key={index} className="d-flex align-items-center mb-2">
              <input
                type="number"
                className="form-control me-2"
                value={num}
                step={step}
                disabled={disabled}
                onChange={(e) =>
                  handleDynamicChange(index, e.target.value)
                }
                placeholder={placeholder}
                required={required}
                onWheel={(e) => e.currentTarget.blur()}
              />
              <button
                type="button"
                className="btn btn-danger btn-sm me-2"
                onClick={() => handleRemoveField(index)}
                disabled={disabled}
              >
                Remove
              </button>
              {index === numberValues.length - 1 && (
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={handleAddField}
                  disabled={disabled}
                >
                  Add
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <input
          type="number"
          id={name}
          name={name}
          className="form-control"
          value={value}
          disabled={disabled}
          step={step}
          onChange={handleStaticChange}
          placeholder={placeholder}
          required={required}
          onWheel={(e) => e.currentTarget.blur()}
        />
      )}

      {displayedRupees.length > 0 && (
        <div className="form-text">
          {displayedRupees.join(', ')}.
        </div>
      )}
    </div>
  );
};

export default NumberField;
